import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getNextTableScrollBudgetState } from './tableScrollBudgetState';

const TableScrollBudgetContext = createContext(null);

/**
 * 列表页 `useTableScrollY` 实测表体预算是否足以支撑固定布局（`next >= TABLE_BODY_SCROLL_MIN`）。
 * 与视口/路由白名单合成为 `useEnableTableScrollYLayout()`，驱动 MainLayout 等切换 natural-scroll。
 *
 * `budgetAllowsFixedLayout`：
 * - `null`：尚未测量或当前路由无 reporter，布局先按 fixed 探测；
 * - `true`：预算充足；
 * - `false`：预算不足，退回 `main-container--natural-scroll`。
 */
export function TableScrollBudgetProvider({ children }) {
    const { pathname } = useLocation();
    const [budgetAllowsFixedLayout, setBudgetAllowsFixedLayout] = useState(null);

    useEffect(() => {
        if (pathname == null) return;
        setBudgetAllowsFixedLayout(null);
    }, [pathname]);

    const reportTableScrollBudget = useCallback(allows => {
        setBudgetAllowsFixedLayout(prev => getNextTableScrollBudgetState(prev, allows));
    }, []);

    const value = useMemo(
        () => ({
            budgetAllowsFixedLayout,
            reportTableScrollBudget,
        }),
        [budgetAllowsFixedLayout, reportTableScrollBudget],
    );

    return <TableScrollBudgetContext.Provider value={value}>{children}</TableScrollBudgetContext.Provider>;
}

export function useTableScrollBudget() {
    const ctx = useContext(TableScrollBudgetContext);
    if (ctx == null) {
        throw new Error('useTableScrollBudget 必须在 TableScrollBudgetProvider 内使用');
    }
    return ctx;
}
